'use client'

import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'
import { tw } from '@/lib/utils'

interface TocItem {
  sectionId: string
  title: string
}

export function StaticTableOfContents({ toc }: { toc: TocItem[] }) {
  const { t } = useTranslation()
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<string | undefined>(undefined)

  const handleLinkClick = useCallback((e: React.MouseEvent, sectionId: string) => {
    e.preventDefault()
    setIsMobileOpen(false)

    const target = document.getElementById(sectionId)
    if (!target) return

    const top = target.getBoundingClientRect().top
    const scrollY = window.scrollY + top - (top < 0 ? 80 : 0)
    window.scrollTo({ top: scrollY, behavior: 'instant' })
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []

    toc.forEach(({ sectionId }) => {
      const target = document.getElementById(sectionId)
      if (!target) return

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(sectionId)
          }
        },
        { rootMargin: '-20% 0px -79% 0px', threshold: 0 }
      )

      observer.observe(target)
      observers.push(observer)
    })

    return () => observers.forEach((obs) => obs.disconnect())
  }, [toc])

  return (
    <>
      <div className="fixed right-6 bottom-6 z-50 lg:hidden">
        <button
          data-tracking="ignore"
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="bg-lapis hover:bg-lapis/90 focus:ring-lapis rounded-full p-4 text-white shadow-lg transition-all focus:ring-2 focus:ring-offset-2 focus:outline-none"
          aria-label={t('aria.tableOfContents', 'Table of Contents')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
          </svg>
        </button>
      </div>

      {isMobileOpen && (
        <div
          className="bg-overlay fixed inset-0 z-40 transition-opacity lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      <nav
        className={tw(
          'fixed top-0 h-screen lg:relative lg:top-auto lg:mt-4 lg:h-auto',
          'w-72 bg-white shadow-2xl lg:w-full lg:bg-transparent lg:shadow-none',
          'z-50 transition-transform duration-300 ease-in-out lg:z-0',
          'overflow-y-auto',
          isMobileOpen ? 'right-0 translate-x-0' : 'right-0 translate-x-full lg:translate-x-0'
        )}
      >
        <div className="p-6 lg:p-0">
          <h2 className="text-navy/50 mb-4 flex items-center gap-2 text-sm font-bold tracking-widest uppercase">
            <span className="bg-lapis inline-block h-4 w-1 rounded-full" />
            {t('client.toc.title', 'Table of Contents')}
          </h2>

          <ul className="relative ml-1.5 space-y-1 border-l border-neutral-200">
            {toc.map(({ sectionId, title }) => {
              const isActive = activeSection === sectionId
              return (
                <li key={sectionId} className="group relative">
                  <a
                    href={`#${sectionId}`}
                    onClick={(e) => handleLinkClick(e, sectionId)}
                    data-tracking="ignore"
                    className={tw(
                      '-ml-[1px] block rounded-r-md border-l-2 py-1 pr-4 pl-4 text-[1.1rem] transition-all duration-300',
                      isActive
                        ? 'text-lapis bg-lapis/10 border-lapis font-semibold'
                        : 'text-navy/60 hover:text-navy border-transparent hover:border-neutral-300 hover:bg-neutral-50'
                    )}
                  >
                    {title}
                  </a>
                </li>
              )
            })}
          </ul>
        </div>
      </nav>
    </>
  )
}
